import {
    type RecordDataNumber,
    type ReportComponentProps,
    type ReportSelectorData,
    type ReportSeriesData,
    YAxisScaleMap,
} from "@qendo/profile/reports";
import { MoodIconMap, ScaleType } from "@qendo/trackables";
import type { DateArg } from "date-fns";
import { type ReactElement, useMemo, useState } from "react";
import { View } from "react-native";
import { LineChart } from "react-native-gifted-charts";
import { QendoIcon } from "~/components/QendoIcon";
import { Space } from "~/constants/sizing";
import { useReport } from "~/providers/ReportProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { when } from "~/util";
import { setWidthOnLayout } from "~/util/layout";
import {
    extractLineSegments,
    extractReportData,
    getMaxYAxis,
    getMinYAxis,
    getNumberOfSections,
    reportColourProps,
} from "~/util/qendo/reports";

export type ReportProps = {
    labels: string[] | Record<string & DateArg<Date>, string>;
} & ReportComponentProps;

type SeriesSelector = ReportSelectorData & ReportSeriesData;

export const Report = ({ labels, report, data, ...reportProps }: ReportProps) => {
    const { colours } = useTheme();
    const { reports } = useReport();
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth);
    const MoodLabels = useMemo(
        () =>
            Object.values(MoodIconMap).map(
                (name) =>
                    (<QendoIcon name={name} key={name} color={colours.icon.secondary} size={22} />) as ReactElement &
                        string,
            ),
        [colours],
    );

    const primary_data = useMemo(() => {
        const min = getMinYAxis(report.scale, data.points);
        const max = getMaxYAxis(report.scale, data.points);

        return {
            data: extractReportData(data.points, labels),
            max: max - min,
            yAxisOffset: min,
            noOfSections: getNumberOfSections(report.scale, max, min),
            color: colours.reports[0],
            dataPointsColor: colours.reports[0],
            yAxisLabelTexts: report.scale === ScaleType.mood ? MoodLabels : undefined,
            lineSegments: extractLineSegments(data.points),
            noOfSectionsBelowXAxis: 0,
        };
    }, [data.points, report.scale, labels, colours, MoodLabels]);
    const main_scale = YAxisScaleMap[report.scale];
    const extra_data_props = useMemo(() => {
        if (!data.compare) {
            return;
        }

        const compare: SeriesSelector[] = data.compare.map((compare) => ({
            ...compare,
            ...(reports.find((v) => v.trackable_id === compare.trackable_id) as ReportSelectorData),
        }));
        const reportColours = reportColourProps(colours, data.compare);
        const others = compare.filter(({ scale }) => YAxisScaleMap[scale] === main_scale);

        return {
            ...reportColours,
            ...Object.fromEntries(
                others.flatMap(({ points }, idx) => [
                    [`data${idx + 2}`, extractReportData(points, labels)],
                    [`lineSegments${idx + 2}`, extractLineSegments(points)],
                ]),
            ),
            maxValue: Math.max(primary_data.max, ...others.map(({ scale, points }) => getMaxYAxis(scale, points))),
            yAxisOffset: Math.min(
                primary_data.yAxisOffset,
                ...others.map(({ scale, points }) => getMinYAxis(scale, points)),
            ),
            ...when(
                compare.find(({ scale }) => YAxisScaleMap[scale] !== main_scale),
                ({ scale, points, trackable_id }) => {
                    const key = (compare.findIndex((v) => v.trackable_id === trackable_id) + 2) as RecordDataNumber;
                    const color = reportColours[`color${key}`];
                    const min = getMinYAxis(scale, points);
                    const max = getMaxYAxis(scale, points);

                    return {
                        secondaryData: extractReportData(points, labels),
                        secondaryYAxis: {
                            yAxisOffset: min,
                            maxValue: max - min,
                            noOfSections: getNumberOfSections(scale, max, min),
                            yAxisLabelTexts: scale === ScaleType.mood ? MoodLabels : undefined,
                            noOfSectionsBelowXAxis: 0,
                        },
                        secondaryLineConfig: {
                            color,
                            dataPointsColor: color,
                        },
                        parentWidth: width - Space.lg * 2,
                    };
                },
            ),
        };
    }, [
        reports,
        primary_data.max,
        primary_data.yAxisOffset,
        data.compare,
        labels,
        main_scale,
        MoodLabels,
        colours,
        width,
    ]);

    return (
        <View onLayout={onLayout}>
            <LineChart
                {...reportProps}
                adjustToWidth
                parentWidth={width - Space.lg}
                thickness={3}
                yAxisThickness={1}
                xAxisThickness={1}
                rulesType="solid"
                verticalLinesColor={colours.border.primary}
                yAxisColor={colours.border.primary}
                xAxisColor={colours.border.primary}
                xAxisLabelTextStyle={[{ minWidth: 30, fontSize: 12 }, reportProps.xAxisLabelTextStyle]}
                disableScroll
                {...primary_data}
                {...extra_data_props}
            />
        </View>
    );
};
